"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "visuais";

// Tipos para as variants do tooltip
type TooltipVariant = "default" | "error" | "success" | "info" | "popover";

// Configuração das variants com estilos organizados
const tooltipVariants = {
  default: "bg-input/60  border border-border shadow-xl backdrop-blur-xl",
  popover: "bg-popover border border-border shadow-xl backdrop-blur-xl",
  error: "bg-destructive/30 text-white border-destructive/20 backdrop-blur-xl",
  success: "bg-green-600/30 text-white border-green-500/20 backdrop-blur-xl",
  info: "bg-blue-600/30 text-white border-blue-500/20 backdrop-blur-xl",
} as const;

// Estilos base comuns
const baseStyles = "z-[999999] p-2 rounded-lg";

// Estilos de animação para variant info
const infoAnimation =
  "animate-in  fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs text-balance";

function TooltipProvider({
  delayDuration = 500,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  variant = "default",
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & {
  variant?: TooltipVariant;
}) {
  const getVariantStyles = (variant: TooltipVariant) => {
    const baseVariantStyles = tooltipVariants[variant];

    // Variant info tem animações especiais
    if (variant === "info") {
      return cn(baseVariantStyles, infoAnimation);
    }

    return cn(baseVariantStyles, baseStyles);
  };

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(getVariantStyles(variant), className)}
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
export type { TooltipVariant };
